import {
	SERVICE_CELL_WIDTH,
	CHECKBOX_COLUMN_WIDTH,
	SCROLLBAR_GUTTER,
	SCROLLBAR_SIZE,
	TABLE_SCROLL_GAP,
} from 'src/Utils/ColumnSizing';
import {
	useReactTable,
	getCoreRowModel,
	TableOptions,
	CellContext,
	ColumnFiltersState,
	ColumnOrderState,
	Row,
	HeaderGroup,
} from '@tanstack/react-table';
import _ from 'lodash';
import { Button, EmptyPanel } from 'src/UI';
import { ColumnType, NvxDataGridProps, RenderTableArgs } from '.';
import { ContextMenuWindow, ContextMenuItemType } from 'src/Components';
import { FileType } from 'src/Types/File';
import { Hint } from '@redoc/ui-kit';
import { ReactComponent as ClearFilterIcon } from './Components/assets/clearFilter.svg';
import { Table as BTable } from 'reactstrap';
import { TableHeaderGroup } from './Components';
import { TableRow } from './Components/TableRow';
import { useContextMenuHelper } from 'src/Hooks';
import {
	useState,
	useMemo,
	useEffect,
	useCallback,
	useRef,
	FC,
	CSSProperties,
} from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import classNames from 'classnames';
import EmptyCell from './Components/EmptyCell/EmptyCell';
import Filter from './Components/TableFilter/TableFilter';
import TableCell from './Components/TableCell/TableCell';
import './NewGrid.scss';
import { filtersToMap } from './utils';

type ExpandStateType = { [Tkey: string]: boolean };

/** Передаёт единые размеры скроллбара в SCSS */
const TABLE_SCROLL_STYLES = {
	'--rdc-datagrid-scrollbar-size': `${SCROLLBAR_SIZE}px`,
	'--rdc-datagrid-scroll-gap': `${TABLE_SCROLL_GAP}px`,
} as CSSProperties;

/** Ширина обёртки таблицы: доступная ширина контейнера + компенсация полосы прокрутки */
const getWidthTable = (containerWidth: number): number => {
	return (
		containerWidth + SCROLLBAR_GUTTER + SCROLLBAR_SIZE + TABLE_SCROLL_GAP
	);
};

const NvxDataGrid: FC<NvxDataGridProps> = (props) => {
	const {
		marginTop = 80,
		data = [],
		columns,
		noDataText,
		dataKey = 'id',
		sort,
		filter,
		columnSizing,
		styles = {},
		minColumnSize = 150,
		isWait = false,
		isMultipleSelect = false,
		isAllRowsExpanded = false,
		allowFilters = false,
		checkBoxesMode = false,
		rowToolbar,
		selectedRecords = [],
		rowExpanding = false,
		className = '',
		bottomToolbarRender,
		onSelectionChanged,
		setSelectedRecords,
		clearFilters,
		onGetRowClasses,
		onPrepareContextMenu,
		onClick,
		onCalcLink,
	} = props;

	const wrapperRef = useRef(null);
	const resizeObserverRef = useRef<ResizeObserver | null>(null);
	// Замер AutoSizer- нужен как стартовая ширина, пока не отработал свой обсервер
	const autoSizerWidthRef = useRef<number>(0);
	const timer = useRef(null);
	// тут будем хранить временную метку mouseDown'а
	const selectionTimeStampRef = useRef<number>(0);
	const [columnOrder, setColumnOrder] = useState<ColumnOrderState>([]);
	const [isActiveRowToolbarOn, setIsActiveRowToolbarOn] =
		useState<string>('');
	const [scroll, setScroll] = useState<number>(0);
	// Доступная ширина таблицы. Обновляется при ресайзе контейнера, чтобы тянущиеся колонки пересчитывались
	const [containerWidth, setContainerWidth] = useState<number>(0);
	const [contextMenuInfo, onContextMenuHandler, hideContextMenu] =
		useContextMenuHelper(!!onPrepareContextMenu);
	const [rowToolbarMenuInfo, onRowToolbarClickHandler, hideTBMenu] =
		useContextMenuHelper(!!rowToolbar && !!rowToolbar.onPrepareMenu);

	const onScrolled = (e: any) => {
		if (!e.target) {
			return;
		}
		hideContextMenu();
		hideTBMenu();
		if (!e.target.scrollLeft) {
			return;
		}
		if (timer.current) {
			clearTimeout(timer.current);
		} else {
			setTimeout(() => {
				setScroll(e.target.scrollLeft);
			}, 200);
		}
	};

	const [expandState, setExpandState] = useState<ExpandStateType>({});
	const toggleExpanded = (recid: string) => {
		return setExpandState((prev) => {
			return {
				...prev,
				[recid]: !prev[recid],
			};
		});
	};

	const getIsExpanded = (recid: string) => {
		return expandState[recid];
	};

	/**
	 * Вешает ResizeObserver на контейнер, в котором лежит таблица. Свой обсервер нужен
	 * потому, что AutoSizer сообщает о ресайзе через setTimeout, то есть уже
	 * после отрисовки нового layout'а
	 */
	const observeContainer = useCallback((node: HTMLDivElement | null) => {
		resizeObserverRef.current?.disconnect();
		resizeObserverRef.current = null;

		if (!node) {
			return;
		}

		const host = node.parentElement?.parentElement;
		if (!host || typeof ResizeObserver === 'undefined') {
			return;
		}
		const observer = new ResizeObserver(([entry]) => {
			const nextWidth = entry.contentRect.width;
			const nextStyleWidth = `${getWidthTable(nextWidth)}px`;
			if (node.style.width !== nextStyleWidth) {
				node.style.width = nextStyleWidth;
			}
			setContainerWidth(nextWidth);
		});
		observer.observe(host);
		resizeObserverRef.current = observer;
	}, []);

	/**
	 * Суммарная ширина служебных колонок (не входящих в расчёт tanstack):
	 * левая- разворот/чекбокс, правая- тулбар строки/пустая ячейка шапки
	 * Нужна, чтобы тянущиеся колонки не выходили за пределы контейнера
	 */
	const getServiceColumnsWidth = (): number => {
		const hasLeftHeaderCell =
			!!clearFilters || rowExpanding || checkBoxesMode;
		const leftHeader = hasLeftHeaderCell ? SERVICE_CELL_WIDTH : 0;
		const leftBody = checkBoxesMode
			? CHECKBOX_COLUMN_WIDTH
			: rowExpanding
				? SERVICE_CELL_WIDTH
				: 0;
		const left = Math.max(leftHeader, leftBody);
		const right = SERVICE_CELL_WIDTH;
		return left + right;
	};

	const getColumnMinWidth = (column: ColumnType): number => {
		return column.minWidth ?? minColumnSize;
	};

	/** Предпочтительная ширина колонки: фиксированная(дата/compact)- своя,
	 *  тянущаяся- её минимум (сверх него она растягивается на свободное место) */
	const getColumnPreferredWidth = (column: ColumnType): number => {
		return column.width ? +column.width : getColumnMinWidth(column);
	};

	/**
	 * Вычисляет ширину всех видимых колонок одним расчётом (flex-модель):
	 *  - хватает места -> тянущиеся колонки растягиваются на остаток, фиксированные стоят на своей ширине;
	 *  - не хватает -> все колонки равномерно ужимаются от предпочтительной
	 *    ширины к минимуму пропорционально запасу сжатия, пока не влезет
	 *    или пока все не упрутся в минимум (тогда горизонтальный скролл);
	 *  - вручную заресайзенные колонки занимают свою ширину и в распределении не участвуют
	 */
	const getColumnWidths = (): Record<string, number> => {
		const savedSizes = columnSizing?.state ?? {};
		const visibleColumns = columns.filter((c) => {
			return c.isVisible !== false;
		});
		const result: Record<string, number> = {};

		const availableTableWidth = containerWidth || autoSizerWidthRef.current;

		const getSavedWidth = (c: ColumnType): number => {
			return Math.max(savedSizes[c.id], getColumnMinWidth(c));
		};

		// Без известной ширины контейнера- отдаём предпочтительные размеры
		if (!availableTableWidth) {
			visibleColumns.forEach((c) => {
				result[c.id] = savedSizes[c.id]
					? getSavedWidth(c)
					: getColumnPreferredWidth(c);
			});
			return result;
		}

		// Заресайзенные вручную колонки фиксируются и не участвуют в расчёте
		let occupied = 0;
		const dynamicColumns: ColumnType[] = [];
		visibleColumns.forEach((c) => {
			if (savedSizes[c.id]) {
				const width = getSavedWidth(c);
				result[c.id] = width;
				occupied += width;
			} else {
				dynamicColumns.push(c);
			}
		});

		const budget =
			availableTableWidth - occupied - getServiceColumnsWidth();
		const totalPreferred = dynamicColumns.reduce((sum, c) => {
			return sum + getColumnPreferredWidth(c);
		}, 0);
		const diff = budget - totalPreferred;

		if (diff >= 0) {
			// Есть свободное место- растягиваем тянущиеся колонки на остаток
			const flexCount =
				dynamicColumns.filter((c) => {
					return !c.width;
				}).length || 1;
			dynamicColumns.forEach((c) => {
				result[c.id] =
					getColumnPreferredWidth(c) +
					(!c.width ? diff / flexCount : 0);
			});
		} else {
			// Места не хватает- равномерно ужимаем колонки к их минимуму
			const shrinkNeeded = -diff;
			const totalRoom = dynamicColumns.reduce((sum, c) => {
				return (
					sum + (getColumnPreferredWidth(c) - getColumnMinWidth(c))
				);
			}, 0);
			const ratio =
				totalRoom > 0 ? Math.min(shrinkNeeded / totalRoom, 1) : 0;
			dynamicColumns.forEach((c) => {
				const room = getColumnPreferredWidth(c) - getColumnMinWidth(c);
				result[c.id] = getColumnPreferredWidth(c) - room * ratio;
			});
		}
		return result;
	};

	/** Следим за данными и флагом и перестраиваем список свернутых/развернутых строк */
	useEffect(() => {
		const state: ExpandStateType = {};
		data.forEach((d) => {
			return (state[d.recid] = isAllRowsExpanded);
		});
		setExpandState(state);
	}, [data, isAllRowsExpanded]);

	useEffect(() => {
		hideContextMenu();
		hideTBMenu();
	}, [data]);

	/** Отрисовываем ячейку таблицы с данными  */
	const cellRender = (props: CellContext<FileType, any>) => {
		const column = columns.find((c) => {
			return c.id === props.column.id;
		});
		return (
			<TableCell
				isExpand={getIsExpanded(props.row.original.id)}
				link={onCalcLink ? onCalcLink(props.row.original) : ''}
				column={column}
				data={props.row.original}
				value={props.getValue()}
				width={props.column.getSize()}
			/>
		);
	};

	// Приводит изначальный формат описания колонок к формату ReactTable
	const preparedColumns = useMemo(() => {
		const columnWidths = getColumnWidths();
		return columns
			.filter((c) => {
				return c.isVisible !== false;
			})
			.map((column: ColumnType) => {
				return {
					...column,
					accessorKey: column.id,
					enableSorting: column.sortable,
					type: column.type || 'string',
					size: columnWidths[column.id] ?? getColumnMinWidth(column),
					minSize: getColumnMinWidth(column),
					maxSize: Number.MAX_SAFE_INTEGER,
					enableColumnFilter: column.filterable,
					cell: cellRender,
					footer: (props: any) => {
						return props.column.id;
					},
				};
			});
		// eslint-disable-next-line
	}, [columns, containerWidth, columnSizing?.state]);

	// Генерируем объект описывающий видимость колонок
	useEffect(() => {
		const cols = _.sortBy(
			columns.filter((c) => {
				return c.isVisible !== false;
			}),
			[
				(o) => {
					return o.position;
				},
			],
		).map((c: ColumnType) => {
			return c.id;
		});
		setColumnOrder(cols);
	}, [columns]);

	/////////////////////// EVENTS ///////////////////////////////////////////////////////////////////////

	const onPrepareContextMenuHandler = (
		data: unknown,
	): ContextMenuItemType[] => {
		if (onPrepareContextMenu) {
			return onPrepareContextMenu(data, selectedRecords);
		}
		return [];
	};

	const onPrepareRowTbMenuHandler = (
		data: unknown,
	): ContextMenuItemType[] => {
		if (rowToolbar?.onPrepareMenu) {
			return rowToolbar?.onPrepareMenu(data, selectedRecords);
		}
		return [];
	};

	/////////////////////// EVENTS ///////////////////////////////////////////////////////////////////////

	/** Проверка на наличие изменений в фильтрах, относительно значений по умолчанию */
	const checkChanged = () => {
		if (!filter) {
			return false;
		}

		const current = filtersToMap(filter.state);
		const initial = filtersToMap(filter.initialState ?? []);
		return !_.isEqual(current, initial);
	};

	const onClearFilters = () => {
		if (clearFilters) {
			clearFilters();
		}
	};

	const renderClearFilter = () => {
		if (!clearFilters && !rowExpanding && !checkBoxesMode) {
			return null;
		} else if (!clearFilters || !checkChanged()) {
			return <EmptyCell isHeader width={32} />;
		}

		return (
			<th className="clear_filter" style={{ width: '32px' }}>
				<Hint messageText="Сбросить все фильтры" placement="top">
					<Button
						id="btn_clearfilters"
						outline
						onClick={onClearFilters}
					>
						<ClearFilterIcon />
					</Button>
				</Hint>
			</th>
		);
	};

	/** Отрисовка фильтра */
	const renderFilter = (columnId: string) => {
		return (
			<Filter columnId={columnId} columnTypes={columns} filter={filter} />
		);
	};

	/**  Рендер шапки таблицы */
	const renderTableHeader = (group: HeaderGroup<FileType>, index: number) => {
		return (
			<TableHeaderGroup
				key={'headerGroup_' + index}
				group={group}
				sort={sort}
				minColumnSize={minColumnSize}
				allowFilters={allowFilters}
				renderFirstToolbar={renderClearFilter}
				renderFilter={renderFilter}
			/>
		);
	};

	/**  Рендер строки таблицы */
	const renderRow = (row: Row<FileType>) => {
		return (
			<TableRow
				key={row.id}
				row={row}
				dataKey={dataKey}
				getIsExpanded={getIsExpanded}
				selectionTimeStampRef={selectionTimeStampRef}
				onContextMenuHandler={onContextMenuHandler}
				toggleExpanded={toggleExpanded}
				columnOrder={columnOrder}
				isActiveRowToolbarOn={isActiveRowToolbarOn}
				setIsActiveRowToolbarOn={setIsActiveRowToolbarOn}
				onRowToolbarClickHandler={onRowToolbarClickHandler}
				onClick={onClick}
				onGetRowClasses={onGetRowClasses}
				selectedRecords={selectedRecords}
				setSelectedRecords={setSelectedRecords}
				isMultipleSelect={isMultipleSelect}
				onSelectionChanged={onSelectionChanged}
				rowExpanding={rowExpanding}
				checkBoxesMode={checkBoxesMode}
				rowToolbar={rowToolbar}
			/>
		);
	};

	/**  Рендер строк таблицы */
	const renderTableRows = (rows: Row<FileType>[]) => {
		if (rows.length) {
			return rows.map((row) => {
				return renderRow(row);
			});
		} else {
			return null;
		}
	};

	const renderLoading = () => {
		return (
			<div className="rdc_table__emptypanel">
				<div className="rdct_table-loader rdc_spiner" />
				<p className="emptypanel_text">Ищем дела...</p>
			</div>
		);
	};

	const renderNoData = () => {
		return (
			<EmptyPanel
				className="rdc_table__emptypanel"
				isRow
				hideIcon={
					!!noDataText &&
					typeof noDataText !== 'string' &&
					typeof noDataText !== 'number'
				}
			>
				{noDataText}
			</EmptyPanel>
		);
	};

	const renderMessages = (rowsCount?: number) => {
		if (isWait) {
			return renderLoading();
		} else if (!rowsCount) {
			return renderNoData();
		} else {
			return null;
		}
	};

	// Опции таблицы
	const gridOptions: TableOptions<FileType> = useMemo(() => {
		const option: TableOptions<FileType> = {
			data,
			state: {
				columnOrder,
				columnSizing:
					!!columnSizing && columnSizing.state
						? columnSizing.state
						: undefined,
			},
			getCoreRowModel: getCoreRowModel(),
			columns: preparedColumns,
			enableColumnResizing: true,
			columnResizeMode: 'onChange',
		};

		if (!!columnSizing && !!columnSizing?.setColumnSize) {
			option.onColumnSizingChange = columnSizing.setColumnSize;
		}

		return option;
		// eslint-disable-next-line
	}, [preparedColumns, data, columnSizing?.state]);

	const table = useReactTable(gridOptions);
	const rows = table.getRowModel().rows;

	if (!preparedColumns.length) {
		return null;
	}

	const renderTable = (props: RenderTableArgs) => {
		const { height, width, scaledWidth } = props;

		// Запоминаем замер AutoSizer- он нужен как стартовая ширина обёртки
		autoSizerWidthRef.current = width;

		const heightTable =
			height -
			marginTop +
			SCROLLBAR_GUTTER +
			SCROLLBAR_SIZE +
			TABLE_SCROLL_GAP;

		return (
			<div
				id="rdct_table"
				ref={observeContainer}
				onScrollCapture={onScrolled}
				className={classNames(
					'rdc_datagrid_wrapper new_datagrid_wrapper body-small',
					className,
				)}
				style={{
					height: heightTable,
					paddingRight: SCROLLBAR_GUTTER,
					paddingBottom: SCROLLBAR_GUTTER,
					...TABLE_SCROLL_STYLES,
					...styles,
				}}
			>
				<BTable responsive style={{ width: '100%' }}>
					{table.getHeaderGroups().map(renderTableHeader)}
					<tbody>
						{renderTableRows(rows)}

						<tr className="row_toolbar nothovered">
							<td
								className="more_btn"
								style={{
									transform: `translate( calc(-50% + ${scaledWidth / 2 + scroll}px), 0`,
								}}
							>
								{!!data.length &&
									!!bottomToolbarRender &&
									bottomToolbarRender()}
							</td>
						</tr>
					</tbody>
				</BTable>
				{renderMessages(rows.length)}
			</div>
		);
	};

	return (
		<>
			<AutoSizer ref={wrapperRef}>{renderTable}</AutoSizer>
			{!!rowToolbar?.onPrepareMenu && (
				<ContextMenuWindow
					right={false}
					paddingX={-25}
					onPrepareContextMenu={onPrepareRowTbMenuHandler}
					{...rowToolbarMenuInfo}
					onClose={() => {
						return setIsActiveRowToolbarOn('');
					}}
					onOutSideClick={(e) => {
						setIsActiveRowToolbarOn('');
						hideTBMenu();
					}}
				/>
			)}
			{!!onPrepareContextMenu && (
				<ContextMenuWindow
					onClose={() => {
						return setIsActiveRowToolbarOn('');
					}}
					onPrepareContextMenu={onPrepareContextMenuHandler}
					{...contextMenuInfo}
				/>
			)}
		</>
	);
};

export default NvxDataGrid;
